import React, { useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { Payment, AffaireAllocation, AffairePaymentStatsEnhanced } from '../../types';

interface AcceptPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: Payment;
  affaire?: AffaireAllocation;
  affairePaymentStats?: Record<string, AffairePaymentStatsEnhanced>;
  onAccept: (paymentId: number, affaireId: string, note: string) => Promise<void>;
}

export const AcceptPaymentModal: React.FC<AcceptPaymentModalProps> = ({
  isOpen,
  onClose,
  payment,
  affaire,
  affairePaymentStats,
  onAccept
}) => {
  const [acceptanceNote, setAcceptanceNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const formatAmount = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Check if this is a multi-payment affaire and get overall stats
  const affaireId = affaire?.affaire_id || payment.affaire_id;
  const stats = affaireId ? affairePaymentStats?.[affaireId] : null;
  const isMultiPayment = stats && stats.paymentCount > 1 && stats.expectedAmount;

  const calculateDifference = () => {
    if (isMultiPayment && stats && stats.expectedAmount !== null) {
      // Multi-payment: use overall stats
      return stats.totalAmount - stats.expectedAmount;
    }
    // Single payment: use per-payment values
    if (!affaire?.expected_amount) return 0;
    return affaire.amount_allocated - affaire.expected_amount;
  };

  // Get the amounts to display
  const receivedAmount = isMultiPayment && stats ? stats.totalAmount : (affaire?.amount_allocated || payment.amount);
  const expectedAmount = isMultiPayment && stats ? stats.expectedAmount : affaire?.expected_amount;

  const difference = calculateDifference();
  const differencePercentage = expectedAmount
    ? Math.abs((difference / expectedAmount) * 100).toFixed(1)
    : 0;

  const isOverpayment = difference > 0;
  const isUnderpayment = difference < 0;

  const handleSubmit = async () => {
    if (!acceptanceNote.trim()) {
      setError('Veuillez ajouter une note explicative');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onAccept(
        payment.id, 
        affaire?.affaire_id || payment.affaire_id || '', 
        acceptanceNote
      );
      onClose();
      setAcceptanceNote('');
    } catch (err) {
      setError('Erreur lors de l\'acceptation du paiement');
      console.error('Error accepting payment:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all">
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full ${
                isOverpayment ? 'bg-amber-100' : isUnderpayment ? 'bg-orange-100' : 'bg-green-100'
              }`}>
                {isOverpayment || isUnderpayment ? (
                  <ExclamationTriangleIcon className={`h-5 w-5 ${
                    isOverpayment ? 'text-amber-600' : 'text-orange-600'
                  }`} />
                ) : (
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900">
                  Accepter le {isOverpayment ? 'sur-paiement' : isUnderpayment ? 'sous-paiement' : 'paiement'}
                </h3>
                
                <div className="mt-4 space-y-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Transaction:</span>
                        <span className="font-medium text-gray-900">{payment.transaction_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Affaire:</span>
                        <span className="font-medium text-gray-900">
                          {affaireId || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {isMultiPayment ? `Total reçu (${stats?.paymentCount} paiements):` : 'Montant reçu:'}
                        </span>
                        <span className="font-medium text-gray-900">
                          {formatAmount(receivedAmount)}
                        </span>
                      </div>
                      {expectedAmount && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Montant attendu:</span>
                            <span className="font-medium text-gray-900">
                              {formatAmount(expectedAmount)}
                            </span>
                          </div>
                          <div className="flex justify-between pt-2 border-t border-gray-200">
                            <span className="text-gray-600">
                              {isMultiPayment ? 'Différence globale:' : 'Différence:'}
                            </span>
                            <span className={`font-medium ${
                              isOverpayment ? 'text-amber-600' : isUnderpayment ? 'text-orange-600' : 'text-green-600'
                            }`}>
                              {difference > 0 ? '+' : ''}{formatAmount(difference)} ({differencePercentage}%)
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {(isOverpayment || isUnderpayment) && (
                    <div className={`rounded-md p-3 ${
                      isOverpayment ? 'bg-amber-50' : 'bg-orange-50'
                    }`}>
                      <p className={`text-sm ${
                        isOverpayment ? 'text-amber-800' : 'text-orange-800'
                      }`}>
                        <strong>Attention:</strong> Ce paiement présente une différence de {formatAmount(Math.abs(difference))} 
                        par rapport au montant attendu. Veuillez expliquer la raison de cette acceptation.
                      </p>
                    </div>
                  )}

                  <div>
                    <label htmlFor="acceptance-note" className="block text-sm font-medium text-gray-700 mb-2">
                      Note d'acceptation <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="acceptance-note"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder={
                        isOverpayment 
                          ? "Ex: Client a payé des frais supplémentaires convenus..." 
                          : isUnderpayment 
                          ? "Ex: Accord commercial pour réduction de 10%..." 
                          : "Ex: Paiement conforme à la facture..."
                      }
                      value={acceptanceNote}
                      onChange={(e) => setAcceptanceNote(e.target.value)}
                      required
                    />
                    {error && (
                      <p className="mt-2 text-sm text-red-600">{error}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !acceptanceNote.trim()}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                isSubmitting || !acceptanceNote.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              }`}
            >
              {isSubmitting ? 'Acceptation...' : 'Accepter et marquer comme résolu'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};